import type { UserSummary } from '@/lib/api/generated/model';
import { deriveId, renameCascade, shouldFollow, takenIds } from './identifiers';
import { autoLayout } from './layout';
import { DONE_NODE_ID, START_NODE_ID, UNASSIGNED_PHASE_ID } from './types';
import type {
  DraftPhase,
  DraftStep,
  DraftStepType,
  DraftTransition,
  Point,
  WorkflowDraft,
} from './types';

/**
 * Every edit the builder can make, as pure functions of a draft.
 *
 * Written as `(args) => (draft) => draft` rather than as reducer action objects
 * so each one is a plain function that can be unit-tested without a store, and
 * so the call site reads as the intention — `edit(addStep({type: 'ai_step'}))`.
 * The reducer stays four cases wide and keeps owning the one thing that is
 * genuinely its business: what counts as a single undo entry.
 *
 * None of these sets `dirty`; the reducer does that for all of them at once,
 * because forgetting it in one operation is a silent data-loss bug.
 */

export type DraftChange = (draft: WorkflowDraft) => WorkflowDraft;

/** Default names, so a new node is legible before anyone edits it. */
/**
 * Put `steps` into the phase named by `phaseId`, creating it if it is absent.
 *
 * Three operations need this — adding a step, moving one between phases, and
 * deleting a phase that still holds steps — and each had its own copy of the
 * same literal. A holding pen spelled slightly differently in one of them would
 * be a second `$unassigned` phase that looks right and behaves like a stranger.
 *
 * The phase is created on demand and never before it is needed, so an empty
 * holding pen is not left behind for the user to wonder about (FR-041).
 */
function intoPhase(
  phases: readonly DraftPhase[],
  phaseId: string,
  steps: readonly DraftStep[],
): DraftPhase[] {
  if (phases.some((phase) => phase.id === phaseId)) {
    return phases.map((phase) =>
      phase.id === phaseId
        ? { ...phase, steps: [...phase.steps, ...steps] }
        : phase,
    );
  }
  return [
    ...phases,
    {
      id: phaseId,
      idPinned: true,
      // A created phase is named for reading, not for its identifier. The only
      // id that reaches here now is `DEFAULT_PHASE_NAME` (or the pen, for a
      // draft written before 060-era builds), and `deriveId` turns "New Phase"
      // into `new-phase` when the draft is serialised.
      name: phaseId === UNASSIGNED_PHASE_ID ? 'Unassigned' : phaseId,
      steps: [...steps],
    },
  ];
}

/**
 * Drop the holding pen once nothing is parked in it — `intoPhase`'s other half.
 *
 * The pen is created on demand and must stop existing on the same terms, and
 * the reason is the service rather than tidiness: `$unassigned` is a reserved
 * id that validation exempts *only* at the draft tier (service V-12). An
 * emptied pen left in the document therefore publishes as "Phase id
 * '$unassigned' uses the reserved '$' prefix" — an identifier the user never
 * typed, on a phase the strip stops drawing the moment it is empty. Nothing on
 * screen to fix, and no delete for a phase that is ours.
 *
 * Only the pen. A user's own empty phase is a column they created deliberately
 * and expect to still be there.
 */
function withoutEmptyHoldingPen(phases: readonly DraftPhase[]): DraftPhase[] {
  return phases.filter(
    (phase) => phase.id !== UNASSIGNED_PHASE_ID || phase.steps.length > 0,
  );
}

/**
 * Where a step goes when the caller names no phase, and the draft to put it in.
 *
 * Replaces the holding pen. A step used to fall into `$unassigned`, which is a
 * reserved id the service exempts only at the draft tier and then refuses to
 * publish (V-16) — so the very first step an author dropped put the workflow
 * into a state it could not leave without knowing about a concept nobody had
 * shown them.
 *
 * The last real phase, when there is one: an author adding without a selection
 * is almost always continuing the stage they were last working in, and minting
 * a fresh phase per unselected drop would litter the strip with columns nobody
 * asked for. Otherwise one is created through `addPhase`, so the created phase
 * is identical to a hand-made one — a derived, uniquified id and the same
 * display name — rather than a lookalike carrying its own name as its id.
 */
function withFallbackPhase(draft: WorkflowDraft): {
  draft: WorkflowDraft;
  phaseId: string;
} {
  const last = draft.phases
    .filter((phase) => phase.id !== UNASSIGNED_PHASE_ID)
    .at(-1);
  if (last) return { draft, phaseId: last.id };

  const phaseId = nextPhaseId(draft);
  return { draft: addPhase()(draft), phaseId };
}

const DEFAULT_NAMES: Record<DraftStepType, string> = {
  ai_step: 'Untitled AI Step',
  human_gate: 'Untitled Human Gate',
  linked_workflow: 'Untitled Sub Workflow',
};

// --- Steps -------------------------------------------------------------------

/**
 * Add a step, in a phase or in the holding pen (FR-011, FR-029).
 *
 * A new step has no connections and no prompt. Both are deliberate: the service
 * lets a draft hold either, and pre-wiring a step to whatever happened to be
 * nearby would be guessing at the author's intent while looking like they had
 * already made the choice.
 *
 * A human gate gets `mode: 'always_human'`, because a gate that can be resolved
 * by an AI is the special case and should be chosen, not defaulted into.
 */
export function addStep(options: {
  type: DraftStepType;
  phaseId?: string;
  at?: Point;
  name?: string;
}): DraftChange {
  return (draft) => {
    const name = options.name ?? DEFAULT_NAMES[options.type];
    const fallback =
      options.phaseId === undefined ? withFallbackPhase(draft) : null;
    const target = fallback?.draft ?? draft;
    const phaseId = options.phaseId ?? fallback?.phaseId ?? '';

    // Derived against the draft that already holds any phase created above, so
    // a step and its brand-new phase cannot collide on an id.
    const id = deriveId(name, takenIds(target));

    const step: DraftStep = {
      id,
      // Not pinned: until someone edits the id by hand it follows the name, so
      // renaming "Untitled AI Step" to "Specify" gives `specify` for free.
      idPinned: false,
      name,
      type: options.type,
      prompt: '',
      llm: null,
      image: null,
      mode: options.type === 'human_gate' ? 'always_human' : null,
      // A new gate routes to nobody. Unassigned is a normal state, not an
      // incomplete one — the workflow is publishable without it (FR-025).
      assigneeUserId: null,
      assignee: null,
      // A new linked-workflow step names no target. Unlike gate mode, there is
      // no sensible default target workflow — the author must pick one.
      workflow: null,
      transitions: [],
    };

    const phases = intoPhase(target.phases, phaseId, [step]);

    return {
      ...target,
      phases,
      layout: { ...target.layout, [id]: options.at ?? { x: 0, y: 0 } },
    };
  };
}

/**
 * Remove a step, and everything that referred to it (FR-015).
 *
 * The cascade is the whole point. A step's own transitions go with it, and so
 * does **every transition pointing at it** — leaving those behind would produce
 * a document the service refuses to store, so the user would discover the
 * problem at save time with no idea which arrow was at fault.
 *
 * The declared start moves too, if it was this step. A start naming a deleted
 * step is the same dangling reference wearing a different hat.
 */
export function deleteStep(stepId: string): DraftChange {
  return (draft) => {
    const layout = { ...draft.layout };
    delete layout[stepId];

    return {
      ...draft,
      phases: withoutEmptyHoldingPen(
        draft.phases.map((phase) => ({
          ...phase,
          steps: phase.steps
            .filter((step) => step.id !== stepId)
            .map((step) => ({
              ...step,
              transitions: step.transitions.filter(
                (transition) => transition.to !== stepId,
              ),
            })),
        })),
      ),
      layout,
      start: draft.start === stepId ? null : draft.start,
    };
  };
}

/** Switch a step between the two declarable types. */
export function changeStepType(
  stepId: string,
  type: DraftStepType,
): DraftChange {
  return (draft) => ({
    ...draft,
    phases: draft.phases.map((phase) => ({
      ...phase,
      steps: phase.steps.map((step) =>
        step.id === stepId
          ? {
              ...step,
              type,
              // A mode on an AI step is meaningless and the service refuses it;
              // becoming a gate needs one, and always-human is the safe default.
              mode:
                type === 'human_gate' ? (step.mode ?? 'always_human') : null,
              // An image override on a human gate is meaningless and the
              // service refuses it, the same rule `mode` follows in reverse.
              image: type === 'ai_step' ? step.image : null,
              // Dropped when a gate stops being one, for the same reason `mode`
              // is: the service refuses an assignee on a non-gate, and the
              // database CHECK refuses it underneath that.
              assigneeUserId:
                type === 'human_gate' ? step.assigneeUserId : null,
              assignee: type === 'human_gate' ? step.assignee : null,
              // Dropped when a step stops being a linked workflow. Unlike gate
              // mode, there is no default to fall back to when becoming one —
              // the author must pick a target, so this clears to null either way.
              workflow:
                type === 'linked_workflow' ? (step.workflow ?? null) : null,
              startChildren:
                type === 'linked_workflow'
                  ? (step.startChildren ?? null)
                  : null,
              // Guidance for a scheduler a step that is not linked does not have.
              schedulingInstructions:
                type === 'linked_workflow'
                  ? (step.schedulingInstructions ?? null)
                  : null,
            }
          : step,
      ),
    })),
  });
}

/** Move a step between phases, including into and out of the holding pen. */
export function moveStepToPhase(
  stepId: string,
  phaseId: string,
  at?: Point,
): DraftChange {
  return (draft) => {
    const step = findStep(draft, stepId);
    if (!step) return draft;

    // The pen is not a destination. Nothing in the UI offers it any more, but
    // this is the operation every one of those paths funnels through, so the
    // refusal belongs here too — a fourth caller added later should not be able
    // to recreate `$unassigned` by passing its id.
    if (phaseId === UNASSIGNED_PHASE_ID) return draft;

    const withoutIt = draft.phases.map((phase) => ({
      ...phase,
      steps: phase.steps.filter((candidate) => candidate.id !== stepId),
    }));

    const phases = intoPhase(withoutEmptyHoldingPen(withoutIt), phaseId, [
      step,
    ]);

    return {
      ...draft,
      phases,
      // Offsets are band-relative, so crossing a band boundary means the old
      // offset now describes a different place. Rebasing is not optional.
      layout: at ? { ...draft.layout, [stepId]: at } : draft.layout,
    };
  };
}

// --- Transitions -------------------------------------------------------------

/**
 * Connect two nodes (FR-019, FR-023, FR-024).
 *
 * **An edge drawn from Start is not a transition.** It declares which step the
 * process begins at, replacing whatever was declared before — a workflow has
 * exactly one starting step, so this is a set rather than an add. Modelling it
 * as a transition would produce a `$start` step the service synthesizes and
 * refuses to be told about (research D17).
 *
 * Everything else adds a transition with a generated key and an empty
 * condition. Empty is correct: the condition is prose the author has to write,
 * and inventing one would put words in their mouth that publication then
 * accepts.
 */
export function connect(source: string, target: string): DraftChange {
  return (draft) => {
    if (source === START_NODE_ID) {
      return { ...draft, start: target === DONE_NODE_ID ? null : target };
    }
    // Nothing leaves Done, and nothing may target Start.
    if (source === DONE_NODE_ID || target === START_NODE_ID) return draft;

    const step = findStep(draft, source);
    if (!step) return draft;
    // Only a Human Gate may lead back to itself: its "unresolved" decision keeps
    // the task waiting for the person. An AI step or a sub-workflow that loops
    // onto itself would just run again with nothing changed, so a connection
    // dropped back on its own step is ignored (the service refuses it too).
    if (target === source && step.type !== 'human_gate') return draft;
    // A connection drawn again while the last one to that step still has no
    // condition is the same connection twice, not a second reason to go there.
    // Adding it would leave two blanks the agent could never tell apart; the
    // canvas selects the waiting one instead (see `blankTransitionTo`).
    if (blankTransitionTo(draft, source, target) !== null) return draft;

    const key = deriveTransitionKey(step, target);

    return {
      ...draft,
      phases: draft.phases.map((phase) => ({
        ...phase,
        steps: phase.steps.map((candidate) =>
          candidate.id === source
            ? {
                ...candidate,
                transitions: [
                  ...candidate.transitions,
                  {
                    key,
                    to: target,
                    when: '',
                    label: null,
                    traversalLimit: null,
                  },
                ],
              }
            : candidate,
        ),
      })),
    };
  };
}

/**
 * The transition from `source` to `target` still waiting for its condition, if any.
 *
 * Several transitions may lead to one step on purpose, each a different reason
 * to go there (the library's V-model workflow does this). A blank one is not a
 * reason yet, so drawing the same connection again finds it rather than making
 * a second blank: `connect` adds nothing and the canvas selects this one.
 */
export function blankTransitionTo(
  draft: WorkflowDraft,
  source: string,
  target: string,
): string | null {
  const blank = findStep(draft, source)?.transitions.find(
    (transition) => transition.to === target && !transition.when.trim(),
  );
  return blank?.key ?? null;
}

/**
 * A key for a new transition, unique within its source step.
 *
 * Named after the target, which is what an author would call it — `to_review`
 * rather than `transition_2`. Uniqueness is per step, so two steps may both
 * have a `to_review`.
 */
function deriveTransitionKey(step: DraftStep, target: string): string {
  const taken = new Set(step.transitions.map((transition) => transition.key));
  const base = deriveId(
    target === DONE_NODE_ID ? 'finish' : `to ${target}`,
    new Set<string>(),
  );
  return deriveId(base, taken);
}

/**
 * The nodes a step's transition may point at, in the order to offer them.
 *
 * A rule about the document rather than about any one control, which is why it
 * is here and not inline in the panel: `connect` enforces the same boundaries
 * when the canvas draws an edge, and two statements of one rule is how they
 * come to disagree.
 *
 * Excluded: Start, which nothing may target, and the step itself — `connect`
 * would accept a self-transition and the service refuses the loop it makes, so
 * offering it is offering something the document cannot honour. Done comes
 * last: it is where a workflow has to be able to end, not the first thing to
 * reach for.
 */
export function transitionTargets(
  draft: WorkflowDraft,
  stepId: string,
): { id: string; name: string }[] {
  const steps = draft.phases
    .flatMap((phase) => phase.steps)
    .filter((step) => step.id !== stepId)
    .map((step) => ({ id: step.id, name: step.name }));

  return [...steps, { id: DONE_NODE_ID, name: 'Done' }];
}

export function deleteTransition(stepId: string, key: string): DraftChange {
  return (draft) => ({
    ...draft,
    phases: draft.phases.map((phase) => ({
      ...phase,
      steps: phase.steps.map((step) =>
        step.id === stepId
          ? {
              ...step,
              transitions: step.transitions.filter(
                (transition) => transition.key !== key,
              ),
            }
          : step,
      ),
    })),
  });
}

// --- Arrangement -------------------------------------------------------------

/**
 * Put a node at an offset within its band.
 *
 * Used for both the frames of a drag and its final position; which of those it
 * is, is the caller's decision expressed by `edit` vs `editTransient`, not
 * something this function can know.
 */
export function moveNode(nodeId: string, to: Point): DraftChange {
  return (draft) => ({
    ...draft,
    layout: { ...draft.layout, [nodeId]: to },
  });
}

/**
 * Re-place every step from the shape of the graph, discarding the arrangement.
 *
 * The same `autoLayout` that places a workflow nobody has arranged yet, which
 * is the point: "tidy" and "as this would have been drawn" are one arrangement
 * rather than two that merely resemble each other, and one canvas cannot end up
 * tidying into a layout the other would not have produced (FR-057).
 *
 * The map is rebuilt rather than patched, so a position left behind by a
 * deleted step goes with it. Bands need no attention of their own: `bandLayout`
 * derives every phase's box from the steps inside it, so re-placing the steps
 * re-fits the lanes around them.
 *
 * Destructive by nature — one call discards every position the user chose by
 * hand. That is why it is an ordinary `DraftChange` and nothing more: it goes
 * through the same history as every other edit, so undo puts the arrangement
 * back, and nothing reaches the service until the draft is saved.
 */
export function tidyLayout(): DraftChange {
  return (draft) => ({
    ...draft,
    layout: autoLayout({ phases: draft.phases, start: draft.start }),
  });
}

// --- Identifiers -------------------------------------------------------------

/**
 * Set a step's id by hand, cascading every reference (FR-035).
 *
 * Pins the id, which stops it following the name from here on: the user has
 * taken it over, and quietly overwriting their choice on the next rename would
 * be worse than never having offered the convenience.
 */
export function setStepId(oldId: string, newId: string): DraftChange {
  return (draft) => {
    const cascaded = renameCascade(draft, oldId, newId);
    return {
      ...cascaded,
      phases: cascaded.phases.map((phase) => ({
        ...phase,
        steps: phase.steps.map((step) =>
          step.id === newId ? { ...step, idPinned: true } : step,
        ),
      })),
    };
  };
}

// --- Shared ------------------------------------------------------------------

export function findStep(
  draft: WorkflowDraft,
  stepId: string,
): DraftStep | undefined {
  for (const phase of draft.phases) {
    const found = phase.steps.find((step) => step.id === stepId);
    if (found) return found;
  }
  return undefined;
}

export function phaseOfStep(
  draft: WorkflowDraft,
  stepId: string,
): string | undefined {
  return draft.phases.find((phase) =>
    phase.steps.some((step) => step.id === stepId),
  )?.id;
}

// --- Field edits -------------------------------------------------------------

/**
 * Set one field on one step.
 *
 * Committed on blur or Enter, never per keystroke (research D2). Typing a
 * prompt would otherwise produce one history entry per character, and undo
 * would mean "delete the last letter" — fifty presses to walk back a sentence.
 */
export function setStepField<K extends keyof DraftStep>(
  stepId: string,
  field: K,
  value: DraftStep[K],
): DraftChange {
  return (draft) => ({
    ...draft,
    phases: draft.phases.map((phase) => ({
      ...phase,
      steps: phase.steps.map((step) =>
        step.id === stepId ? { ...step, [field]: value } : step,
      ),
    })),
  });
}

/**
 * The id `renameStep` would produce, without performing the rename.
 *
 * Exported because a rename can change the id out from under whatever is
 * pointing at it — the URL selection, most importantly. A caller that renames
 * and then keeps selecting the *old* id loses its subject mid-edit, which is
 * exactly what happened: renaming a step from the inspector silently swapped
 * the panel to the workflow-level fields.
 */
export function renamedStepId(
  draft: WorkflowDraft,
  stepId: string,
  name: string,
): string {
  const step = findStep(draft, stepId);
  if (!step || !shouldFollow(step, draft.status)) return stepId;

  const taken = takenIds(draft);
  taken.delete(stepId);
  return deriveId(name, taken);
}

/**
 * Rename a step, letting its id follow unless the id has been taken over.
 *
 * The convenience that makes ids bearable: nobody wants to type one, and a
 * workflow full of `untitled-ai-step-3` is what happens when they are not
 * offered for free. Following stops the moment the user edits the id by hand,
 * or the moment the workflow is published and real tasks reference it.
 */
export function renameStep(stepId: string, name: string): DraftChange {
  return (draft) => {
    const step = findStep(draft, stepId);
    if (!step) return draft;

    const named = setStepField(stepId, 'name', name)(draft);
    if (!shouldFollow(step, draft.status)) return named;

    const taken = takenIds(named);
    taken.delete(stepId);
    const nextId = deriveId(name, taken);
    if (nextId === stepId) return named;

    // The cascade, not a bare id assignment: transition targets, the declared
    // start, and the layout key all point at the old id.
    const cascaded = renameCascade(named, stepId, nextId);
    return {
      ...cascaded,
      phases: cascaded.phases.map((phase) => ({
        ...phase,
        steps: phase.steps.map((candidate) =>
          // Still following — this rename was derived, not chosen.
          candidate.id === nextId
            ? { ...candidate, idPinned: false }
            : candidate,
        ),
      })),
    };
  };
}

/** Set one field on one transition. */
export function setTransitionField<K extends keyof DraftTransition>(
  stepId: string,
  key: string,
  field: K,
  value: DraftTransition[K],
): DraftChange {
  return (draft) => ({
    ...draft,
    phases: draft.phases.map((phase) => ({
      ...phase,
      steps: phase.steps.map((step) =>
        step.id === stepId
          ? {
              ...step,
              transitions: step.transitions.map((transition) =>
                transition.key === key
                  ? { ...transition, [field]: value }
                  : transition,
              ),
            }
          : step,
      ),
    })),
  });
}

/** Set a workflow-level field — the inspector's view when nothing is selected. */
export function setWorkflowField<K extends 'name' | 'description' | 'image'>(
  field: K,
  value: WorkflowDraft[K],
): DraftChange {
  return (draft) => ({ ...draft, [field]: value });
}

// --- Phases ------------------------------------------------------------------

/**
 * Add a phase, at the end of the ordered ones.
 *
 * Appended before `$unassigned` rather than after it: the holding pen is drawn
 * as a trailing band and is not a stage of the process, so a new phase landing
 * after it would read as coming *later* than "not yet placed".
 */
export const DEFAULT_PHASE_NAME = 'New phase';

/**
 * The id `addPhase` would derive right now.
 *
 * Exported so a caller can select the phase it is about to create without
 * guessing the id — the derivation uniquifies, so the second "New phase" is
 * `new-phase-2` and a hardcoded guess would select the wrong one.
 */
export function nextPhaseId(
  draft: WorkflowDraft,
  name = DEFAULT_PHASE_NAME,
): string {
  return deriveId(name, takenIds(draft));
}

/**
 * Empty the holding pen into a real phase.
 *
 * Applied when a draft is loaded, because a draft written by an earlier build
 * can still have steps parked in `$unassigned`. Nothing in the builder routes
 * steps there any more, so a parked step would be drawn in a band the author
 * cannot add to, cannot rename and cannot delete — while still being the reason
 * the workflow refuses to publish (V-16). Left alone it is a fault with no
 * visible cause and no reachable fix.
 *
 * The steps land in the last real phase, or in a created one when the draft has
 * none — the same rule `addStep` follows, so a re-homed draft is indistinguish-
 * able from one authored today.
 *
 * A change, not a silent repair: the caller marks the draft dirty so the author
 * sees a modified document they can inspect and undo, rather than a save that
 * happened to them. Returns the draft untouched when there is nothing parked,
 * so an ordinary load stays clean.
 */
export function rehomeParkedSteps(): DraftChange {
  return (draft) => {
    const pen = draft.phases.find((phase) => phase.id === UNASSIGNED_PHASE_ID);
    if (!pen || pen.steps.length === 0) return draft;

    const withoutPen = {
      ...draft,
      phases: draft.phases.filter((phase) => phase.id !== UNASSIGNED_PHASE_ID),
    };
    const { draft: target, phaseId } = withFallbackPhase(withoutPen);

    return { ...target, phases: intoPhase(target.phases, phaseId, pen.steps) };
  };
}

export function addPhase(name = DEFAULT_PHASE_NAME): DraftChange {
  return (draft) => {
    const id = deriveId(name, takenIds(draft));
    const phase = { id, idPinned: false, name, steps: [] };

    const ordered = draft.phases.filter(
      (candidate) => candidate.id !== UNASSIGNED_PHASE_ID,
    );
    const unassigned = draft.phases.filter(
      (candidate) => candidate.id === UNASSIGNED_PHASE_ID,
    );

    return { ...draft, phases: [...ordered, phase, ...unassigned] };
  };
}

/** The id `renamePhase` would produce — see `renamedStepId` for why. */
export function renamedPhaseId(
  draft: WorkflowDraft,
  phaseId: string,
  name: string,
): string {
  const phase = draft.phases.find((candidate) => candidate.id === phaseId);
  if (!phase || phaseId === UNASSIGNED_PHASE_ID) return phaseId;
  if (!shouldFollow(phase, draft.status)) return phaseId;

  const taken = takenIds(draft);
  taken.delete(phaseId);
  return deriveId(name, taken);
}

/**
 * Rename a phase, letting its id follow while it still may.
 *
 * The same rule as a step's name, and for the same reason — a phase id is a
 * board column's identity, so a published workflow's phases are frozen too.
 */
export function renamePhase(phaseId: string, name: string): DraftChange {
  return (draft) => {
    const phase = draft.phases.find((candidate) => candidate.id === phaseId);
    // The holding pen's name is ours, not the user's.
    if (!phase || phaseId === UNASSIGNED_PHASE_ID) return draft;

    const renamed = {
      ...draft,
      phases: draft.phases.map((candidate) =>
        candidate.id === phaseId ? { ...candidate, name } : candidate,
      ),
    };
    if (!shouldFollow(phase, draft.status)) return renamed;

    const taken = takenIds(renamed);
    taken.delete(phaseId);
    const nextId = deriveId(name, taken);
    if (nextId === phaseId) return renamed;

    return {
      ...renamed,
      phases: renamed.phases.map((candidate) =>
        candidate.id === phaseId
          ? { ...candidate, id: nextId, idPinned: false }
          : candidate,
      ),
    };
  };
}

/** Set a phase's id by hand, pinning it. */
export function setPhaseId(oldId: string, newId: string): DraftChange {
  return (draft) => {
    if (oldId === UNASSIGNED_PHASE_ID || oldId === newId) return draft;
    return {
      ...draft,
      phases: draft.phases.map((phase) =>
        phase.id === oldId ? { ...phase, id: newId, idPinned: true } : phase,
      ),
    };
  };
}

/**
 * Move a phase to a new position among the ordered ones (FR-043).
 *
 * Phase order *is* board column order, which is the reason this exists: the
 * canvas is where a user can see the process, so it is where they should be
 * able to say what order its stages come in.
 *
 * `$unassigned` never moves — it is pinned to the trailing edge.
 */
export function reorderPhase(phaseId: string, toIndex: number): DraftChange {
  return (draft) => {
    if (phaseId === UNASSIGNED_PHASE_ID) return draft;

    const ordered = draft.phases.filter(
      (phase) => phase.id !== UNASSIGNED_PHASE_ID,
    );
    const unassigned = draft.phases.filter(
      (phase) => phase.id === UNASSIGNED_PHASE_ID,
    );

    const from = ordered.findIndex((phase) => phase.id === phaseId);
    if (from === -1) return draft;

    const moved = [...ordered];
    const [phase] = moved.splice(from, 1);
    moved.splice(Math.max(0, Math.min(toIndex, moved.length)), 0, phase);

    return { ...draft, phases: [...moved, ...unassigned] };
  };
}

/**
 * Delete a phase, and the steps in it.
 *
 * The steps used to survive, parked in the holding pen, on the reasoning that a
 * phase is an organising device and taking the shelf away is not a reason to
 * throw out what was on it. That was coherent, but it made the pen
 * load-bearing: the escape hatch existed *because* this operation needed
 * somewhere to put things. With the pen gone the alternatives are to destroy
 * the steps or to push them into a neighbouring phase they were never part of,
 * and a phase silently absorbing another's steps is the harder one to notice.
 *
 * Guarded rather than silent: `PhaseInspector` refuses outright while a task
 * sits on one of these steps, and asks first when any step would be lost. Undo
 * restores the phase and its steps in one press, until the draft is saved.
 */
export function deletePhase(phaseId: string): DraftChange {
  return (draft) => {
    if (phaseId === UNASSIGNED_PHASE_ID) return draft;

    const phase = draft.phases.find((candidate) => candidate.id === phaseId);
    if (!phase) return draft;

    const remaining = draft.phases.filter(
      (candidate) => candidate.id !== phaseId,
    );
    if (phase.steps.length === 0) return { ...draft, phases: remaining };

    // The steps go with the phase.
    //
    // They used to survive it, parked in `$unassigned`, on the reasoning that
    // deleting a stage is not a request to delete the work in it. That was
    // coherent but it made the pen load-bearing: the escape hatch existed
    // because this operation needed somewhere to put things. With the pen gone
    // the alternatives are to destroy the steps or to push them into a
    // neighbouring phase they were never part of, and a phase silently
    // absorbing another's steps is the harder one to notice or undo.
    //
    // Guarded rather than silent: `PhaseInspector` refuses outright when a task
    // sits on one of these steps, and asks first when any step would be lost.
    // Undo covers it until the draft is saved.
    const doomed = new Set(phase.steps.map((step) => step.id));
    const layout = { ...draft.layout };
    for (const id of doomed) delete layout[id];

    return {
      ...draft,
      phases: remaining.map((candidate) => ({
        ...candidate,
        // Transitions pointing into the deleted phase would otherwise survive
        // as arrows to nothing, which publishes as an unresolvable target —
        // the same cleanup `deleteStep` does, applied to a set.
        steps: candidate.steps.map((step) => ({
          ...step,
          transitions: step.transitions.filter(
            (transition) => !doomed.has(transition.to),
          ),
        })),
      })),
      layout,
      start:
        draft.start !== null && doomed.has(draft.start) ? null : draft.start,
    };
  };
}

/**
 * Route a gate to a person, or to nobody.
 *
 * Takes the resolved person alongside the id so the panel and the YAML preview
 * can show a name without a second lookup — the draft is what those render from,
 * and it is edited far more often than it is saved.
 */
export function setStepAssignee(
  stepId: string,
  userId: string | null,
  person: UserSummary | null,
): DraftChange {
  return (draft) => ({
    ...draft,
    phases: draft.phases.map((phase) => ({
      ...phase,
      steps: phase.steps.map((step) =>
        step.id === stepId
          ? { ...step, assigneeUserId: userId, assignee: person }
          : step,
      ),
    })),
  });
}
